import React, { useEffect, useState } from "react";
import { openWhatsApp } from "../../lib/whatsapp";
import { Loader2, Ticket, Copy, Plus, Users, Gift, Wheat, Sprout, Award, Trash2, Pencil, Eye, EyeOff, X, Save, MessageCircle, Search, Download, UserCog } from "lucide-react";
import {
  adminListLoyaltyUsers,
  adminDeleteLoyaltyUser,
  adminCreateLoyaltyCode,
  adminListLoyaltyCodes,
  adminListLoyaltyRewards,
  adminCreateLoyaltyReward,
  adminUpdateLoyaltyReward,
  adminDeleteLoyaltyReward,
  previewWaTemplate,
} from "../../lib/api";
import { toast } from "../../hooks/use-toast";
import CustomersPanel from "./CustomersPanel";

const LEVEL_COLOR = {
  seme: "bg-amber-100 text-amber-800",
  germoglio: "bg-emerald-100 text-emerald-800",
  fiore: "bg-pink-100 text-pink-800",
  frutto: "bg-red-100 text-red-800",
  raccolta: "bg-[#4a1c17]/10 text-[#4a1c17]",
};

export default function LoyaltyEditor() {
  const [sub, setSub] = useState("customers"); // customers | users | codes | rewards
  const [users, setUsers] = useState([]);
  const [codes, setCodes] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [u, c, r] = await Promise.all([
        adminListLoyaltyUsers(),
        adminListLoyaltyCodes(),
        adminListLoyaltyRewards(),
      ]);
      setUsers(u.users || []);
      setCodes(c.codes || []);
      setRewards(r.rewards || []);
    } catch (e) {
      toast({ title: "Errore caricamento fidelity", description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-5" data-testid="admin-loyalty-editor">
      <div>
        <h2 className="font-serif text-2xl font-bold text-[#2b1410]">Fidelity</h2>
        <p className="text-sm text-[#7c5a4e]">Cashback in € · Seme → Germoglio → Spiga → Raccolta</p>
      </div>

      <div className="inline-flex flex-wrap rounded-full bg-[#faf3ee] p-1 border border-[#e8dcc8] gap-1">
        <button
          onClick={() => setSub("customers")}
          data-testid="loyalty-tab-customers"
          className={`px-4 py-1.5 rounded-full text-sm font-semibold inline-flex items-center gap-1.5 ${sub === "customers" ? "bg-[#4a1c17] text-white" : "text-[#4a1c17]"}`}
        >
          <UserCog className="h-4 w-4" /> Clienti
        </button>
        <button
          onClick={() => setSub("users")}
          data-testid="loyalty-tab-users"
          className={`px-4 py-1.5 rounded-full text-sm font-semibold inline-flex items-center gap-1.5 ${sub === "users" ? "bg-[#4a1c17] text-white" : "text-[#4a1c17]"}`}
        >
          <Users className="h-4 w-4" /> Iscritti Fidelity ({users.length})
        </button>
        <button
          onClick={() => setSub("rewards")}
          data-testid="loyalty-tab-rewards"
          className={`px-4 py-1.5 rounded-full text-sm font-semibold inline-flex items-center gap-1.5 ${sub === "rewards" ? "bg-[#4a1c17] text-white" : "text-[#4a1c17]"}`}
        >
          <Gift className="h-4 w-4" /> Esperienze ({rewards.length})
        </button>
      </div>

      {sub === "customers" ? (
        <CustomersPanel />
      ) : loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#4a1c17]" /></div>
      ) : sub === "users" ? (
        <UsersPanel users={users} onChange={load} />
      ) : sub === "codes" ? (
        <CodesPanel codes={codes} onChange={load} />
      ) : (
        <RewardsPanel rewards={rewards} onChange={load} />
      )}
    </div>
  );
}

function UsersPanel({ users, onChange }) {
  // WhatsApp send: send a codice cashback link
  const [waFor, setWaFor] = useState(null);
  const [waStamps, setWaStamps] = useState(1);
  const [waDays, setWaDays] = useState(30);
  const [waBusy, setWaBusy] = useState(false);
  // Search
  const [q, setQ] = useState("");

  const normalisePhone = (raw) => {
    // wa.me wants digits only, no leading + or spaces. Assume Italy (+39) if
    // the number starts with a 3 (mobile) and has no country prefix yet.
    const digits = String(raw || "").replace(/\D/g, "");
    if (!digits) return null;
    if (digits.startsWith("00")) return digits.slice(2);
    if (digits.startsWith("39")) return digits;
    if (digits.startsWith("3") && digits.length >= 9) return "39" + digits;
    return digits; // trust already-formatted numbers otherwise
  };

  const sendWhatsApp = async (user) => {
    const phone = normalisePhone(user.phone);
    if (!phone) { toast({ title: "Numero non valido" }); return; }
    setWaBusy(true);
    try {
      const r = await adminCreateLoyaltyCode(Number(waStamps), Number(waDays));
      const redeemUrl = `${window.location.origin}/fidelity`;
      const nextLv = user.next_level?.label_it;
      const nextLine = nextLv
        ? `\nDopo il riscatto potresti passare al livello *${nextLv}* 🌱`
        : "";
      // Render the fidelity_code template with the real context so any
      // edits made in "Messaggi" propagate here without a code change.
      const { rendered: msg } = await previewWaTemplate("fidelity_code", null, {
        name: user.name,
        stamps: r.stamps,
        code: r.code,
        email: user.email,
        days: waDays,
        redeem_url: redeemUrl,
        next_level_line: nextLine,
      });
      openWhatsApp(phone, msg);
      toast({ title: `Codice ${r.code} generato · WhatsApp aperto` });
      setWaFor(null); setWaStamps(1); setWaDays(30);
      onChange();
    } catch (e) {
      toast({ title: "Errore", description: e?.response?.data?.detail || e.message });
    } finally { setWaBusy(false); }
  };

  const removeUser = async (user) => {
    if (!window.confirm(
      `Eliminare definitivamente il cliente "${user.name}" (${user.email})?\n\n` +
      `Verranno persi:\n· € ${(user.balance_euros ?? 0).toFixed(2)} di credito residuo\n· € ${(user.lifetime_spent_euros ?? 0).toFixed(2)} di spesa storica\n\nQuesta operazione non è reversibile.`
    )) return;
    try {
      await adminDeleteLoyaltyUser(user.id);
      toast({ title: "Cliente eliminato" });
      onChange();
    } catch (e) {
      toast({ title: "Errore", description: e?.response?.data?.detail || e.message });
    }
  };

  // Filter users by search query on name/email/phone (case-insensitive).
  const filtered = React.useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return users;
    return users.filter((u) =>
      (u.name || "").toLowerCase().includes(needle) ||
      (u.email || "").toLowerCase().includes(needle) ||
      (u.phone || "").toLowerCase().includes(needle)
    );
  }, [users, q]);

  const exportCsv = () => {
    const rows = filtered.length ? filtered : users;
    const header = ["Nome", "Email", "Telefono", "Livello", "Credito €", "Spesa totale €", "Data iscrizione"];
    const escape = (v) => {
      const s = v == null ? "" : String(v);
      return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [header.join(";")];
    rows.forEach((u) => {
      lines.push([
        u.name,
        u.email,
        u.phone || "",
        u.level?.label_it || "Seme",
        (u.balance_euros ?? 0).toFixed(2),
        (u.lifetime_spent_euros ?? 0).toFixed(2),
        u.created_at ? new Date(u.created_at).toLocaleDateString("it-IT") : "",
      ].map(escape).join(";"));
    });
    // Prepend UTF-8 BOM so Excel opens accented chars correctly.
    const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const today = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `amici-del-pozzo_${today}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast({ title: `Esportati ${rows.length} clienti` });
  };

  if (users.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-[#e8dcc8] bg-white p-10 text-center text-sm text-[#7c5a4e]">
        Ancora nessun cliente registrato. Condividi il QR code o il link <code className="bg-[#faf3ee] px-1 py-0.5 rounded">/fidelity</code> per farli iscrivere.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Toolbar: search + export */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="h-4 w-4 text-[#7c5a4e] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca per nome, email o telefono…"
            data-testid="loyalty-users-search"
            className="w-full rounded-full border border-[#e8dcc8] bg-white pl-9 pr-9 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4a1c17]/20"
          />
          {q && (
            <button
              onClick={() => setQ("")}
              aria-label="Cancella ricerca"
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#7c5a4e] hover:text-[#4a1c17]"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <button
          onClick={exportCsv}
          data-testid="loyalty-users-export-csv"
          className="inline-flex items-center gap-1.5 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-white text-sm px-4 py-2"
          title="Scarica la lista come CSV (Excel-compatibile)"
        >
          <Download className="h-4 w-4" /> Esporta CSV
        </button>
      </div>

      {q && (
        <div className="text-xs text-[#7c5a4e]">
          {filtered.length} risultat{filtered.length === 1 ? "o" : "i"} su {users.length} · <button onClick={() => setQ("")} className="underline hover:text-[#4a1c17]">azzera</button>
        </div>
      )}

      <div className="rounded-2xl border border-[#e8dcc8] bg-white overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-10 text-center text-sm text-[#7c5a4e]">
            Nessun cliente trovato con "{q}".
          </div>
        ) : (
        <table className="w-full text-sm">
          <thead className="bg-[#faf3ee] text-[#7c5a4e] uppercase text-[10px] tracking-wider">
            <tr>
              <th className="text-left px-3 py-2">Cliente</th>
              <th className="text-left px-3 py-2 hidden sm:table-cell">Email</th>
              <th className="text-center px-3 py-2">Livello</th>
              <th className="text-right px-3 py-2">Credito</th>
              <th className="text-right px-3 py-2 hidden md:table-cell">Spesa</th>
              <th className="text-right px-3 py-2">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((u) => {
              const lvKey = u.level?.key || "seme";
              const cls = LEVEL_COLOR[lvKey] || "bg-slate-100 text-slate-700";
              return (
                <React.Fragment key={u.id}>
                <tr className="border-t border-[#e8dcc8]">
                  <td className="px-3 py-2">
                    <div className="font-semibold text-[#2b1410]">{u.name}</div>
                    <div className="text-[11px] text-[#7c5a4e] sm:hidden truncate">{u.email}</div>
                    {u.phone && <div className="text-[11px] text-[#7c5a4e]">📞 {u.phone}</div>}
                  </td>
                  <td className="px-3 py-2 hidden sm:table-cell text-[#7c5a4e]">{u.email}</td>
                  <td className="px-3 py-2 text-center">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${cls}`}>
                      <Wheat className="h-3 w-3" />
                      {u.level?.label_it || "Seme"}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right font-mono font-bold text-emerald-700">€ {(u.balance_euros ?? 0).toFixed(2)}</td>
                  <td className="px-3 py-2 text-right font-mono text-[#4a1c17] hidden md:table-cell">€ {(u.lifetime_spent_euros ?? 0).toFixed(2)}</td>
                  <td className="px-3 py-2 text-right space-y-1 sm:space-y-0 sm:space-x-1 whitespace-nowrap">
                    <button
                      onClick={() => setWaFor(waFor === u.id ? null : u.id)}
                      disabled={!u.phone}
                      data-testid={`loyalty-wa-btn-${u.id}`}
                      className="inline-flex items-center gap-1 rounded-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-neutral-200 disabled:text-neutral-500 text-white text-[11px] px-2.5 py-1.5"
                      title={u.phone ? "Genera codice e invialo via WhatsApp" : "Il cliente non ha il numero di telefono"}
                    >
                      <MessageCircle className="h-3 w-3" /> WhatsApp
                    </button>
                    <button
                      onClick={() => removeUser(u)}
                      data-testid={`loyalty-delete-user-${u.id}`}
                      className="inline-flex items-center gap-1 rounded-full border border-red-200 hover:bg-red-50 text-red-700 text-[11px] px-2 py-1.5"
                      title="Elimina cliente"
                      aria-label="Elimina cliente"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </td>
                </tr>
                {waFor === u.id && (
                  <tr className="bg-emerald-50">
                    <td colSpan={6} className="px-3 py-3">
                      <div className="flex flex-wrap items-end gap-2">
                        <div className="flex-1 min-w-[220px] text-[11px] text-emerald-900">
                          <div className="font-semibold uppercase tracking-wider mb-1">Invia codice via WhatsApp</div>
                          <div>Genererà un codice usa-e-getta e aprirà WhatsApp con un messaggio pre-compilato per <strong>{u.phone}</strong>. Il cliente inserirà il codice da solo su <code>/fidelity</code>.</div>
                        </div>
                        <div>
                          <label className="block text-[10px] uppercase font-semibold text-emerald-900">Timbri</label>
                          <input type="number" min={1} max={20} value={waStamps}
                            onChange={(e) => setWaStamps(e.target.value)}
                            data-testid="loyalty-wa-stamps"
                            className="w-20 rounded-lg border border-emerald-300 px-2 py-1.5 bg-white" />
                        </div>
                        <div>
                          <label className="block text-[10px] uppercase font-semibold text-emerald-900">Scade (giorni)</label>
                          <input type="number" min={1} max={365} value={waDays}
                            onChange={(e) => setWaDays(e.target.value)}
                            className="w-20 rounded-lg border border-emerald-300 px-2 py-1.5 bg-white" />
                        </div>
                        <button
                          onClick={() => sendWhatsApp(u)}
                          disabled={waBusy || !waStamps || waStamps < 1}
                          data-testid={`loyalty-wa-send-${u.id}`}
                          className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-4 py-2 disabled:opacity-50"
                        >
                          {waBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <MessageCircle className="h-3 w-3" />}
                          Genera e invia
                        </button>
                        <button
                          onClick={() => setWaFor(null)}
                          className="rounded-full border border-emerald-300 text-emerald-800 text-xs px-3 py-2"
                        >Annulla</button>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
        )}
      </div>
    </div>
  );
}

function CodesPanel({ codes, onChange }) {
  const [stamps, setStamps] = useState(1);
  const [days, setDays] = useState(30);
  const [busy, setBusy] = useState(false);
  const [lastCode, setLastCode] = useState(null);

  const create = async () => {
    setBusy(true);
    try {
      const r = await adminCreateLoyaltyCode(Number(stamps), Number(days));
      setLastCode(r);
      toast({ title: `Codice creato: ${r.code}` });
      onChange();
    } catch (e) {
      toast({ title: "Errore", description: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  const copy = (code) => {
    navigator.clipboard.writeText(code);
    toast({ title: "Copiato" });
  };

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-[#e8dcc8] bg-white p-4">
        <div className="flex items-center gap-2 mb-3">
          <Plus className="h-4 w-4 text-[#4a1c17]" />
          <h3 className="font-semibold text-[#2b1410]">Genera un codice usa-e-getta</h3>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-[10px] uppercase font-semibold text-[#7c5a4e]">Timbri erogati</label>
            <input type="number" min={1} max={20} value={stamps}
              onChange={(e) => setStamps(e.target.value)}
              data-testid="loyalty-new-code-stamps"
              className="w-24 rounded-lg border border-[#e8dcc8] px-2 py-1.5 bg-white" />
          </div>
          <div>
            <label className="block text-[10px] uppercase font-semibold text-[#7c5a4e]">Scade in (giorni)</label>
            <input type="number" min={1} max={365} value={days}
              onChange={(e) => setDays(e.target.value)}
              className="w-24 rounded-lg border border-[#e8dcc8] px-2 py-1.5 bg-white" />
          </div>
          <button
            onClick={create}
            disabled={busy}
            data-testid="loyalty-create-code-btn"
            className="rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-white text-sm px-5 py-2 disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin inline" /> : "Genera codice"}
          </button>
        </div>
        {lastCode && (
          <div className="mt-3 rounded-lg bg-emerald-50 border border-emerald-200 p-3 flex items-center gap-2">
            <Ticket className="h-4 w-4 text-emerald-700" />
            <div className="flex-1">
              <div className="text-[11px] text-emerald-800 uppercase tracking-wider">Codice creato</div>
              <div className="font-mono text-lg font-bold text-emerald-900 tracking-widest">{lastCode.code}</div>
              <div className="text-[11px] text-emerald-800">Vale {lastCode.stamps} timbri · scade {new Date(lastCode.expires_at).toLocaleDateString("it-IT")}</div>
            </div>
            <button onClick={() => copy(lastCode.code)} className="rounded-full border border-emerald-300 text-emerald-800 text-xs px-3 py-1.5 inline-flex items-center gap-1">
              <Copy className="h-3 w-3" /> Copia
            </button>
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-[#e8dcc8] bg-white overflow-hidden">
        <div className="px-4 py-2 bg-[#faf3ee] text-[#7c5a4e] uppercase text-[10px] tracking-wider font-semibold">
          Storico codici
        </div>
        {codes.length === 0 ? (
          <div className="p-10 text-center text-sm text-[#7c5a4e]">Nessun codice generato.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-[#7c5a4e] uppercase text-[10px] tracking-wider">
              <tr>
                <th className="text-left px-3 py-2">Codice</th>
                <th className="text-center px-3 py-2">Timbri</th>
                <th className="text-center px-3 py-2 hidden sm:table-cell">Creato</th>
                <th className="text-center px-3 py-2 hidden sm:table-cell">Scadenza</th>
                <th className="text-center px-3 py-2">Stato</th>
              </tr>
            </thead>
            <tbody>
              {codes.map((c) => {
                const used = Boolean(c.used_by);
                const expired = c.expires_at && new Date(c.expires_at) < new Date();
                return (
                  <tr key={c.code} className="border-t border-[#e8dcc8]">
                    <td className="px-3 py-2 font-mono font-bold text-[#4a1c17]">
                      <button onClick={() => copy(c.code)} className="inline-flex items-center gap-1.5 hover:underline">
                        {c.code} <Copy className="h-3 w-3" />
                      </button>
                    </td>
                    <td className="px-3 py-2 text-center">{c.stamps}</td>
                    <td className="px-3 py-2 text-center hidden sm:table-cell text-[11px] text-[#7c5a4e]">
                      {c.created_at ? new Date(c.created_at).toLocaleDateString("it-IT") : "—"}
                    </td>
                    <td className="px-3 py-2 text-center hidden sm:table-cell text-[11px] text-[#7c5a4e]">
                      {c.expires_at ? new Date(c.expires_at).toLocaleDateString("it-IT") : "—"}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {used ? (
                        <span className="inline-block rounded-full bg-slate-100 text-slate-700 text-[11px] px-2 py-0.5">Usato</span>
                      ) : expired ? (
                        <span className="inline-block rounded-full bg-red-100 text-red-700 text-[11px] px-2 py-0.5">Scaduto</span>
                      ) : (
                        <span className="inline-block rounded-full bg-emerald-100 text-emerald-700 text-[11px] px-2 py-0.5">Attivo</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}


const LEVEL_OPTIONS = [
  { key: "seme", label: "Seme (0€)" },
  { key: "germoglio", label: "Germoglio (100€)" },
  { key: "spiga", label: "Spiga (500€)" },
  { key: "raccolta", label: "Raccolta (1000€)" },
];

const LEVEL_LABEL = Object.fromEntries(LEVEL_OPTIONS.map(({ key, label }) => [key, label.split(" ")[0]]));

function emptyReward() {
  return { name_it: "", name_en: "", description_it: "", description_en: "", min_level_key: "germoglio", visible: true, order: 0 };
}

function RewardsPanel({ rewards, onChange }) {
  const [editing, setEditing] = useState(null); // reward object or "new"
  const [form, setForm] = useState(emptyReward());
  const [busy, setBusy] = useState(false);

  const startNew = () => { setForm(emptyReward()); setEditing("new"); };
  const startEdit = (r) => {
    setForm({
      name_it: r.name_it || "",
      name_en: r.name_en || "",
      description_it: r.description_it || "",
      description_en: r.description_en || "",
      min_level_key: r.min_level_key || "germoglio",
      visible: r.visible !== false,
      order: r.order || 0,
    });
    setEditing(r);
  };
  const cancel = () => { setEditing(null); setForm(emptyReward()); };

  const save = async () => {
    if (!form.name_it.trim()) { toast({ title: "Nome (IT) obbligatorio" }); return; }
    if (!form.min_level_key) { toast({ title: "Livello richiesto obbligatorio" }); return; }
    setBusy(true);
    try {
      const payload = { ...form, order: Number(form.order), stamps_cost: 0 };
      if (editing === "new") await adminCreateLoyaltyReward(payload);
      else await adminUpdateLoyaltyReward(editing.id, payload);
      toast({ title: editing === "new" ? "Esperienza creata" : "Esperienza aggiornata" });
      cancel();
      onChange();
    } catch (e) {
      toast({ title: "Errore", description: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  const toggleVisible = async (r) => {
    try {
      await adminUpdateLoyaltyReward(r.id, {
        name_it: r.name_it, name_en: r.name_en || "",
        description_it: r.description_it || "", description_en: r.description_en || "",
        min_level_key: r.min_level_key || "germoglio", stamps_cost: 0,
        visible: !r.visible, order: r.order || 0,
      });
      onChange();
    } catch (e) {
      toast({ title: "Errore", description: e?.response?.data?.detail || e.message });
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Eliminare l'esperienza "${r.name_it}"?`)) return;
    try {
      await adminDeleteLoyaltyReward(r.id);
      toast({ title: "Esperienza eliminata" });
      onChange();
    } catch (e) {
      toast({ title: "Errore", description: e?.response?.data?.detail || e.message });
    }
  };

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-[#7c5a4e]">Esperienze esclusive che i clienti sbloccano al livello Fidelity indicato.</p>
        <button
          onClick={startNew}
          data-testid="loyalty-new-reward-btn"
          className="inline-flex items-center gap-1.5 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-white text-sm px-4 py-2"
        >
          <Plus className="h-4 w-4" /> Nuova esperienza
        </button>
      </div>

      {editing && (
        <div className="rounded-2xl border border-[#4a1c17]/30 bg-[#faf3ee] p-4 space-y-3" data-testid="loyalty-reward-form">
          <div className="flex justify-between items-center">
            <h3 className="font-semibold text-[#2b1410]">
              {editing === "new" ? "Nuova esperienza" : `Modifica: ${editing.name_it}`}
            </h3>
            <button onClick={cancel} className="text-[#7c5a4e] hover:text-[#4a1c17]"><X className="h-4 w-4" /></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] uppercase font-semibold text-[#7c5a4e]">Nome IT *</label>
              <input value={form.name_it} onChange={(e) => setF("name_it", e.target.value)}
                data-testid="loyalty-reward-name-it"
                className="w-full rounded-lg border border-[#e8dcc8] px-3 py-2 bg-white" />
            </div>
            <div>
              <label className="block text-[10px] uppercase font-semibold text-[#7c5a4e]">Nome EN</label>
              <input value={form.name_en} onChange={(e) => setF("name_en", e.target.value)}
                className="w-full rounded-lg border border-[#e8dcc8] px-3 py-2 bg-white" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-[10px] uppercase font-semibold text-[#7c5a4e]">Descrizione IT</label>
              <textarea rows={2} value={form.description_it} onChange={(e) => setF("description_it", e.target.value)}
                className="w-full rounded-lg border border-[#e8dcc8] px-3 py-2 bg-white text-sm" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-[10px] uppercase font-semibold text-[#7c5a4e]">Descrizione EN</label>
              <textarea rows={2} value={form.description_en} onChange={(e) => setF("description_en", e.target.value)}
                className="w-full rounded-lg border border-[#e8dcc8] px-3 py-2 bg-white text-sm" />
            </div>
            <div>
              <label className="block text-[10px] uppercase font-semibold text-[#7c5a4e]">Livello richiesto *</label>
              <select
                value={form.min_level_key}
                onChange={(e) => setF("min_level_key", e.target.value)}
                data-testid="loyalty-reward-level"
                className="w-full rounded-lg border border-[#e8dcc8] px-3 py-2 bg-white"
              >
                {LEVEL_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] uppercase font-semibold text-[#7c5a4e]">Ordine visualizzazione</label>
              <input type="number" value={form.order} onChange={(e) => setF("order", e.target.value)}
                className="w-full rounded-lg border border-[#e8dcc8] px-3 py-2 bg-white" />
            </div>
            <label className="sm:col-span-2 inline-flex items-center gap-2 text-sm text-[#2b1410]">
              <input type="checkbox" checked={form.visible} onChange={(e) => setF("visible", e.target.checked)} />
              Visibile ai clienti
            </label>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={cancel} className="rounded-full border border-[#e8dcc8] text-[#7c5a4e] text-sm px-4 py-2">Annulla</button>
            <button
              onClick={save}
              disabled={busy}
              data-testid="loyalty-reward-save"
              className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm px-4 py-2 disabled:opacity-50"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Salva
            </button>
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-[#e8dcc8] bg-white overflow-hidden">
        {rewards.length === 0 ? (
          <div className="p-10 text-center text-sm text-[#7c5a4e]">Nessun premio ancora. Clicca "Nuovo premio" per iniziare.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#faf3ee] text-[#7c5a4e] uppercase text-[10px] tracking-wider">
              <tr>
                <th className="text-left px-3 py-2">Esperienza</th>
                <th className="text-center px-3 py-2">Sblocco a</th>
                <th className="text-center px-3 py-2 hidden sm:table-cell">Ordine</th>
                <th className="text-center px-3 py-2">Visibile</th>
                <th className="text-right px-3 py-2">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {rewards.map((r) => (
                <tr key={r.id} className={`border-t border-[#e8dcc8] ${r.visible === false ? "opacity-60" : ""}`}>
                  <td className="px-3 py-2">
                    <div className="font-semibold text-[#2b1410]">{r.name_it}</div>
                    {r.description_it && <div className="text-[11px] text-[#7c5a4e]">{r.description_it}</div>}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <span className="inline-flex items-center gap-1 rounded-full bg-[#4a1c17]/10 text-[#4a1c17] px-2 py-0.5 text-[11px] font-semibold">
                      {LEVEL_LABEL[r.min_level_key || "germoglio"] || (r.min_level_key || "Germoglio")}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-center hidden sm:table-cell text-[#7c5a4e]">{r.order || 0}</td>
                  <td className="px-3 py-2 text-center">
                    <button
                      onClick={() => toggleVisible(r)}
                      data-testid={`loyalty-reward-toggle-${r.id}`}
                      className={`inline-flex items-center gap-1 rounded-full text-[11px] px-2 py-0.5 ${r.visible !== false ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`}
                    >
                      {r.visible !== false ? <><Eye className="h-3 w-3" /> Sì</> : <><EyeOff className="h-3 w-3" /> No</>}
                    </button>
                  </td>
                  <td className="px-3 py-2 text-right space-x-1">
                    <button
                      onClick={() => startEdit(r)}
                      data-testid={`loyalty-reward-edit-${r.id}`}
                      className="inline-flex items-center gap-1 rounded-full border border-[#e8dcc8] text-[#4a1c17] text-[11px] px-3 py-1.5 hover:bg-[#faf3ee]"
                    >
                      <Pencil className="h-3 w-3" /> Modifica
                    </button>
                    <button
                      onClick={() => remove(r)}
                      data-testid={`loyalty-reward-delete-${r.id}`}
                      className="inline-flex items-center gap-1 rounded-full border border-red-200 text-red-700 text-[11px] px-3 py-1.5 hover:bg-red-50"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
